import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import Swal from "sweetalert2";

const TOUR_VERSION = "v1";
const LS_KEY = "kb_tour_seen";
const SS_KEY = "kb_tour_pending";
const LS_DISMISS_KEY = "kb_tour_dismissed";
const DISMISS_THRESHOLD = 3;

function _getDismissCount() {
  return parseInt(localStorage.getItem(LS_DISMISS_KEY) || "0", 10);
}
function _incrementDismiss() {
  const n = _getDismissCount() + 1;
  localStorage.setItem(LS_DISMISS_KEY, String(n));
  return n;
}
function _resetDismissCount() {
  localStorage.removeItem(LS_DISMISS_KEY);
}

function currentRoute() {
  return window.location.hash.slice(1).split("?")[0];
}

const USER_STEPS = [
  // Dashboard
  { route: "dashboard", element: ".kb-hero-card", popover: { title: "Bienvenido a KNOWFLOW", description: "Tu base de conocimiento interna. Aquí tu equipo encuentra respuestas rápidas sobre procesos, políticas y documentación. Vamos a recorrer las funciones disponibles.", side: "bottom" } },
  { route: "dashboard", element: "#kb-stat-categorias", popover: { title: "Categorías", description: "Muestra a cuántas categorías tenés acceso. Hacé clic en esta tarjeta para ir directo al listado.", side: "bottom" } },
  { route: "dashboard", element: "#kb-stat-ia", popover: { title: "Asistente IA", description: "Acceso rápido al chat con inteligencia artificial. Hacé clic en esta tarjeta para ir directo al asistente.", side: "bottom" } },
  { route: "dashboard", element: ".kb-ai-card", popover: { title: "Chat con el asistente IA", description: "Escribí cualquier pregunta en lenguaje natural. La IA busca en la base de conocimiento y te responde indicando las fuentes consultadas.", side: "right" } },
  { route: "dashboard", element: "#kb-categorias-section", popover: { title: "Explorar por categoría", description: "Navegá el contenido por tema. Cada tarjeta te lleva directamente a los artículos de esa categoría.", side: "top" } },
  { route: "dashboard", element: "#kb-historial-clear", popover: { title: "Historial de lectura", description: "Los últimos artículos que abriste aparecen acá para volver a ellos rápido. Podés limpiar el historial con este botón.", side: "top" } },
  // Knowledge
  { route: "knowledge", element: "#kb-search", popover: { title: "Buscador principal", description: "Buscá por título, contenido o tags. Con el botón de la derecha activás la búsqueda semántica IA, que encuentra resultados aunque no uses las palabras exactas.", side: "bottom" } },
  { route: "knowledge", element: ".kb-cat-nav", popover: { title: "Filtro por categoría", description: "Filtrá el contenido por tema. El número de cada categoría indica cuántos artículos tiene.", side: "right" } },
  { route: "knowledge", element: ".kb-type-filters", popover: { title: "Filtros por tipo", description: "Filtrá por tipo de contenido: texto, PDF, video, imagen o audio. Podés combinar varios tipos a la vez.", side: "right" } },
  { route: "knowledge", element: "[data-view='grid']", popover: { title: "Vista lista o grilla", description: "Cambiá la forma en que se muestran los artículos. La vista en grilla es útil para explorar visualmente.", side: "left" } },
  { route: "knowledge", element: "#kb-articulos-container", popover: { title: "Artículos", description: "El resultado de tu búsqueda o filtro. Hacé clic en el título de cualquier artículo para leerlo.", side: "top" } },
  { route: "knowledge", element: ".kb-sidebar-ai", popover: { title: "Chat IA contextual", description: "Preguntale a la IA directamente desde esta sección. Si tenés una categoría seleccionada, la búsqueda se limita a esa categoría.", side: "left" } },
  { route: "knowledge", element: "#kb-suggest-btn", popover: { title: "Sugerir un tema", description: "Si no encontraste lo que buscabas, hacé clic acá para enviar una sugerencia. Se abre un formulario donde podés describir el tema que falta documentar.", side: "top" } },
];

const ADMIN_ONLY_STEPS = [
  { route: "admin", element: "#adminTabs", popover: { title: "Panel de administración", description: "Desde acá gestionás toda la base de conocimiento: artículos, categorías, usuarios y las sugerencias de los usuarios.", side: "bottom" } },
  { route: "admin", element: "#btn-new-articulo", popover: { title: "Crear un artículo", description: "Hacé clic en este botón para abrir el formulario de creación. Admite texto con formato, PDF, video, imagen, audio y archivos adicionales. El contenido se indexa automáticamente para el asistente IA.", side: "left" } },
  { route: "admin", element: "button[data-tab='categorias']", popover: { title: "Gestionar categorías", description: "Creá y editá categorías con ícono, color y descripción. Podés marcarlas como públicas (visibles para todos los usuarios, sin importar sus permisos).", side: "bottom" } },
  { route: "admin", element: "button[data-tab='usuarios']", popover: { title: "Usuarios y permisos", description: "Administrá los usuarios del sistema y sus permisos por categoría. Un usuario puede tener acceso total o limitado a categorías específicas.", side: "bottom" } },
  { route: "admin", element: "button[data-tab='sugerencias']", popover: { title: "Sugerencias de usuarios", description: "Acá llegan los temas que los usuarios pidieron documentar, tanto desde la búsqueda como desde el asistente IA. Podés revisarlos y eliminar los ya atendidos.", side: "bottom" } },
];

function _getSteps(isAdmin) {
  return isAdmin ? [...USER_STEPS, ...ADMIN_ONLY_STEPS] : [...USER_STEPS];
}

// Primer paso a partir de `from` que está en otra pantalla, o -1 si no queda ninguno.
function _nextRouteIndex(allSteps, from, route) {
  for (let i = from; i < allSteps.length; i++) {
    if (allSteps[i].route !== route) return i;
  }
  return -1;
}

function _goToStep(allSteps, index, isAdmin) {
  sessionStorage.setItem(SS_KEY, JSON.stringify({ stepIndex: index, isAdmin }));
  window.location.hash = allSteps[index].route;
}

function _runFrom(allSteps, startIndex) {
  const route = currentRoute();
  const isAdmin = allSteps.length > USER_STEPS.length;
  // Los pasos cuyo elemento no existe (ej. "Historial" sin artículos leídos) se omiten
  const stepsForPage = allSteps
    .slice(startIndex)
    .map((s, i) => ({ ...s, _globalIndex: startIndex + i }))
    .filter(s => s.route === route && (!s.element || document.querySelector(s.element)));
  // Pantalla donde sigue el recorrido después de esta (-1: termina acá)
  const nextIndex = _nextRouteIndex(allSteps, startIndex, route);

  if (!stepsForPage.length) {
    // Nada para mostrar en esta pantalla: seguir en la próxima o dar el tour por visto
    // (si no, quedaba sin marcar y volvía a arrancar en cada ingreso)
    if (nextIndex >= 0) _goToStep(allSteps, nextIndex, isAdmin);
    else TourService.markSeen();
    return;
  }

  // El último paso de la pantalla dice "Siguiente" si el recorrido continúa en otra
  if (nextIndex >= 0) {
    const last = stepsForPage[stepsForPage.length - 1];
    stepsForPage[stepsForPage.length - 1] = { ...last, popover: { ...last.popover, doneBtnText: "Siguiente" } };
  }

  let _completed = false;
  let _leaving = false;

  const d = driver({
    showProgress: true,
    progressText: "{{current}} de {{total}}",
    nextBtnText: "Siguiente",
    prevBtnText: "Anterior",
    doneBtnText: "Finalizar",
    smoothScroll: true,
    onNextClick: (_el, _step, { state }) => {
      // Quedan pasos en esta pantalla
      if (state.activeIndex < stepsForPage.length - 1) {
        d.moveNext();
        return;
      }
      // Último de la pantalla: pasar a la siguiente o terminar
      if (nextIndex >= 0) {
        _leaving = true;
        d.destroy();
        _goToStep(allSteps, nextIndex, isAdmin);
      } else {
        _completed = true;
        d.moveNext();
      }
    },
    onDestroyed: () => {
      if (_completed) {
        TourService.markSeen();
        _resetDismissCount();
        return;
      }
      // Cambio de pantalla dentro del recorrido: no es un cierre
      if (_leaving) return;
      // Tour cerrado sin completar: contar y eventualmente preguntar
      const count = _incrementDismiss();
      if (count >= DISMISS_THRESHOLD) {
        setTimeout(() => {
          Swal.fire({
            title: "¿Ya conocés la app?",
            html: `Cerraste el tour varias veces sin completarlo. ¿Querés que no aparezca más al iniciar?
                   <br><br>
                   <small class="text-muted">
                     <i class="fas fa-map me-1 text-primary"></i>
                     Siempre podés volver a iniciarlo desde el menú de usuario →
                     <strong>Hacer recorrido</strong>.
                   </small>`,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Sí, no mostrar más",
            cancelButtonText: "Seguir mostrando",
            reverseButtons: true,
          }).then(result => {
            if (result.isConfirmed) {
              TourService.markSeen();
            }
            _resetDismissCount();
          });
        }, 300);
      }
    },
    steps: stepsForPage,
  });

  d.drive();
}

export const TourService = {
  TOUR_VERSION,

  hasSeen() {
    return localStorage.getItem(LS_KEY) === TOUR_VERSION;
  },

  markSeen() {
    localStorage.setItem(LS_KEY, TOUR_VERSION);
  },

  start(isAdmin) {
    document.activeElement?.blur();
    if (currentRoute() !== "dashboard") {
      sessionStorage.setItem(SS_KEY, JSON.stringify({ stepIndex: 0, isAdmin }));
      window.location.hash = "dashboard";
    } else {
      setTimeout(() => _runFrom(_getSteps(isAdmin), 0), 100);
    }
  },

  resumeIfPending() {
    const raw = sessionStorage.getItem(SS_KEY);
    if (!raw) return;
    try {
      const { stepIndex, isAdmin } = JSON.parse(raw);
      sessionStorage.removeItem(SS_KEY);
      setTimeout(() => _runFrom(_getSteps(isAdmin), stepIndex), 400);
    } catch {
      sessionStorage.removeItem(SS_KEY);
    }
  },

  maybeAutoStart(isAdmin) {
    if (!TourService.hasSeen()) {
      setTimeout(() => _runFrom(_getSteps(isAdmin), 0), 400);
    }
  },
};
